import { t } from '@lingui/macro'
import { isThisYear } from 'date-fns'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from 'styled-components'

import { Chip } from '@/@bothrs-ui/web/components/chip/Chip'
import { FlexColumn, Margin, Padding } from '@/@bothrs-ui/web/components/layout'
import { ProfileButton } from '@/@bothrs-ui/web/components/navigation/header/ProfileButton'
import { useEdgeInsets } from '@/@bothrs-ui/web/hooks'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'
import { NotFoundError } from '@/modules/shared/components/not-found-error/NotFoundError'
import { capitalize, formatLocale } from '@/modules/shared/helpers/locale'
import { MessageIndicator } from '@/modules/task/components/message-indicator/MessageIndicator'
import { NativePlatform } from '../../../../capacitor/components/native-platform/NativePlatform'
import { type TimelineContextType, TimelineErrorState } from '../../types/timeline-context.type'
import { IntramurosExpiredError } from '../intramuros/IntramurosExpiredError'
import { IntramurosGeneralError } from '../intramuros/IntramurosGeneralError'
import { TimelineEmpty } from './components/timeline-empty/TimelineEmpty'
import { TimelineEmptyMonth } from './components/timeline-empty-month/TimelineEmptyMonth'
import { TimelineItem } from './components/timeline-item/TimelineItem'
import { calculateStartAnimationIndex, scrollToId, scrollToToday } from './helpers/animation'
import { groupTimelineActivitiesByMonth } from './helpers/groupByDate'
import { mapAppointmentsToTimeline, mapEncountersToTimeline } from './helpers/mapping'
import {
  AnimatedChipContainer,
  StContainer,
  StHeaderAction,
  StScrollContainer,
  StSectionTitle,
} from './Timeline.styled'
import { TimelineMobileActionButton } from './TimelineMobileActionButton'

import type { ActivityCardProps } from '@/modules/shared/components/activity-card/ActivityCard.types'

type TimelineActivity = ActivityCardProps & {
  start: string
}
type TimelineDay = {
  id: string
  date: Date
  activities: TimelineActivity[]
}

const Timeline = ({
  hasIntramurosPermission,
  appointments,
  encounters,
  isLoading,
  error,
  forceReload,
}: TimelineContextType) => {
  const theme = useTheme()
  const { bottom } = useEdgeInsets()
  const location = useLocation()
  const navigate = useNavigate()

  const [hasAutoScrolled, setHasAutoScrolled] = useState(false)
  // Template variables
  // ------------------------------------------------------------------------- /
  const activities = useMemo<TimelineActivity[]>(() => {
    const timelineActivities: TimelineActivity[] = [
      ...mapAppointmentsToTimeline(appointments.value, navigate),
      ...mapEncountersToTimeline(encounters.value, navigate),
    ]

    // Sort by starttime (ignoring date, only on hours and minutes)
    return (
      timelineActivities.sort((a, b) => {
        const aTime = (a.scheduledTime?.getHours() ?? 0) * 60 + (a.scheduledTime?.getMinutes() ?? 0)
        const bTime = (b.scheduledTime?.getHours() ?? 0) * 60 + (b.scheduledTime?.getMinutes() ?? 0)
        return aTime - bTime
      }) ?? []
    )
  }, [appointments.value, encounters.value, navigate])

  const isEmpty = useMemo(() => activities.length === 0 && !isLoading, [activities, isLoading])

  const existsActivityInTimeline = useMemo(() => {
    if (isLoading || isEmpty || hasAutoScrolled) return false

    const path = location.pathname.split('/')
    const id = path.pop()

    if (id) {
      return activities.some((activity) => activity.id.toString() === id)
    }
  }, [activities, hasAutoScrolled, isEmpty, isLoading, location.pathname])

  const currentActivity = useMemo(() => {
    const { pathname } = location
    const pathSegments = pathname.split('/')
    const id = pathSegments.pop()

    return activities.find((activity) => activity.id.toString() === id)
  }, [activities, location])

  // Effects
  // ------------------------------------------------------------------------- /
  useEffect(() => {
    if (isEmpty || isLoading || hasAutoScrolled) return

    setHasAutoScrolled(true)

    const { pathname } = location

    const shouldScrollToToday =
      pathname === '/timeline' || pathname === '/timeline/' || pathname.startsWith('/intramuros')

    if (shouldScrollToToday) {
      scrollToToday()
      return
    }

    if (currentActivity && existsActivityInTimeline) {
      setTimeout(() => scrollToId(`card_${currentActivity?.id}`, true), 100)
      return
    }

    scrollToToday()
  }, [hasAutoScrolled, isEmpty, isLoading, location.pathname, existsActivityInTimeline, location, currentActivity])

  if (error && error === TimelineErrorState.INTRAMUROS_TOKEN_EXPIRED) {
    return (
      <AnimatePresence>
        <IntramurosExpiredError />
      </AnimatePresence>
    )
  }

  if (error && hasIntramurosPermission) {
    return (
      <AnimatePresence>
        <IntramurosGeneralError />
      </AnimatePresence>
    )
  }

  if (error) {
    return (
      <AnimatePresence>
        <NotFoundError onClick={forceReload} />
      </AnimatePresence>
    )
  }

  if (isEmpty) {
    return (
      <Padding
        px={theme.UI.Spacing.Space6}
        pt={theme.UI.Spacing.Space4}
        pb={theme.UI.Spacing.Space4 + bottom}
        style={{ height: '100%' }}
      >
        <AnimatePresence>
          <TimelineEmpty />
        </AnimatePresence>
      </Padding>
    )
  }

  return (
    <StContainer id='timeline'>
      {hasIntramurosPermission ? null : (
        <NativePlatform>
          <StHeaderAction>
            <MessageIndicator />

            <ProfileButton />
          </StHeaderAction>
        </NativePlatform>
      )}

      <StScrollContainer>
        <AnimatePresence mode='wait'>
          <FlexColumn>
            {renderItems(activities)}
            {/* Adding a bottom margin with the height equal to the animation height + chip height to avoid weird animations */}
            <Margin mb={theme.UI.Spacing.Space12} />
          </FlexColumn>
        </AnimatePresence>
        <AnimatedChipContainer {...getDropInProps()} onClick={() => scrollToToday(true)}>
          <Chip type={'Secondary'} glass>
            {t({
              id: 'appointments.timeline.chip',
              comment: 'Chip on timeline',
              message: 'Terug naar vandaag',
            })}
          </Chip>
        </AnimatedChipContainer>
      </StScrollContainer>
      <TimelineMobileActionButton />
    </StContainer>
  )
}

// Render methods
// ------------------------------------------------------------------------- /
function renderItems(activities: TimelineActivity[]) {
  const timelineItems = groupTimelineActivitiesByMonth(activities)

  // Animation helper variables
  let delayOffsetIndex = 0
  const firstStartAnimationIndex = calculateStartAnimationIndex(activities)

  return timelineItems.map((month) => {
    const sectionTitle = capitalize(formatLocale(month.date, isThisYear(month.date) ? 'MMMM' : 'MMMM yyyy'))

    // Copy the current offset and update the value for next iteration
    const monthsOffsetIndex = delayOffsetIndex
    delayOffsetIndex +=
      month.days.reduce((accumulator, current) => {
        return accumulator + current.activities.length
      }, 0) || 0

    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{
          delay: 0.1,
        }}
        key={sectionTitle}
      >
        {month.days.length === 0 ? (
          <TimelineEmptyMonth sectionTitle={sectionTitle} />
        ) : (
          <>
            <StSectionTitle hasActivities={month.days.length === 0}>{sectionTitle}</StSectionTitle>
            {month.days.map((day, dayIndex) => {
              const daysOffsetIndex = month.days.reduce((accumulator, current, index) => {
                if (index < dayIndex) {
                  return accumulator + current.activities.length
                }
                return accumulator
              }, 0)
              const offsetIndex = monthsOffsetIndex + daysOffsetIndex - firstStartAnimationIndex

              return <TimelineItem key={day.id} day={day} animationOffset={offsetIndex} />
            })}
          </>
        )}
        <Padding pt={24} />
        <TimelineMobileActionButton />
      </motion.div>
    )
  })
}

export type { TimelineActivity, TimelineDay }
export { Timeline }
